import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Send, UserCheck, XCircle, Loader2, Clock, AlertTriangle, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Btn, Input, Badge } from "@/components/kit";

const PRIORITY_TONE = { critical: "neg", high: "warn", normal: "primary", low: "muted" };
const STATUS_TONE = { pending: "warn", assigned: "primary", answered: "pos", dismissed: "muted", escalated: "neg" };
const USAGE = [
  { v: "only_conversation", l: "Somente esta conversa" },
  { v: "permanent", l: "Conhecimento permanente" },
  { v: "temporary", l: "Temporário (N dias)" },
  { v: "until_date", l: "Válido até uma data" },
  { v: "from_date", l: "Válido a partir de uma data" },
  { v: "no_reuse", l: "Sem reutilização futura" },
];
const SCOPES = ["employee", "department", "project", "client", "team", "company", "executive",
  "finance_restricted", "hr_restricted", "legal_restricted", "commercial_restricted", "custom"];
const SOURCE_TYPES = ["human_answer", "official_policy", "temporary_guidance", "manager_decision",
  "executive_decision", "verified_fact", "unverified_information", "ai_inference"];
const STATUS_LABEL = { pending: "Pendentes", assigned: "Atribuídas", answered: "Respondidas", dismissed: "Descartadas", escalated: "Escaladas", all: "Todas" };
const STATUS_BADGE = { pending: "Pendente", assigned: "Atribuída", answered: "Respondida", dismissed: "Descartada", escalated: "Escalada" };
const PRIORITY_LABEL = { critical: "Crítica", high: "Alta", normal: "Normal", low: "Baixa" };
const SCOPE_LABEL = { employee: "Funcionário", department: "Departamento", project: "Projeto", client: "Cliente", team: "Equipe", company: "Empresa", executive: "Diretoria", finance_restricted: "Financeiro (restrito)", hr_restricted: "RH (restrito)", legal_restricted: "Jurídico (restrito)", commercial_restricted: "Comercial (restrito)", custom: "Personalizado" };
const SOURCE_LABEL = { human_answer: "Resposta humana", official_policy: "Política oficial", temporary_guidance: "Orientação temporária", manager_decision: "Decisão do gestor", executive_decision: "Decisão da diretoria", verified_fact: "Fato verificado", unverified_information: "Informação não verificada", ai_inference: "Inferência da IA" };
const USAGE_LABEL = Object.fromEntries([
  ["only_conversation", "Somente esta conversa"], ["permanent", "Conhecimento permanente"],
  ["temporary", "Temporário"], ["until_date", "Válido até uma data"],
  ["from_date", "Válido a partir de uma data"], ["no_reuse", "Sem reutilização"]]);
const REASON_LABEL = {
  no_context: "sem base de conhecimento", missing_information: "falta informação",
  ambiguous: "ambíguo", conflict: "informações conflitantes", not_found: "não encontrado",
  document_not_found: "documento não encontrado", no_permission: "sem permissão",
  low_confidence: "baixa confiança", out_of_scope: "fora do escopo", unknown_account: "conta desconhecida",
};
const DEPT_LABEL = {
  commercial: "Comercial", financial: "Financeiro", hr: "RH", legal: "Jurídico",
  admin: "Administrativo", administrative: "Administrativo", operations: "Operações",
};

export default function PendingQuestions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [priority, setPriority] = useState("");
  const [active, setActive] = useState(null); // pending being answered
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (priority) p.set("priority", priority);
    api.get(`/pending?${p.toString()}`).then(({ data }) => setItems(data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [status, priority]);

  const openAnswer = (pq) => {
    setActive(pq);
    setSuggestion(null);
    setForm({ answer_text: "", usage: "only_conversation", scope_type: "employee",
      source_type: "human_answer", validity_days: 30, valid_until: "", valid_from: "", scope_targets: "" });
  };

  const getSuggestion = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/pending/${active.id}/suggest`, {});
      setSuggestion(data);
      setForm((f) => ({ ...f, usage: data.usage || f.usage, scope_type: data.scope_type || f.scope_type,
        source_type: data.source_type || f.source_type,
        validity_days: data.validity_days || f.validity_days }));
      toast.success("Sugestão aplicada — revise e edite antes de enviar");
    } catch (e) { toast.error(apiErr(e)); } finally { setSuggesting(false); }
  };

  const assign = async (pq) => { try { await api.post(`/pending/${pq.id}/assign`, {}); toast.success("Atribuída a você"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const dismiss = async (pq) => { try { await api.post(`/pending/${pq.id}/dismiss`, { reason: "descartada" }); toast.success("Descartada"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const submitAnswer = async () => {
    if (!form.answer_text?.trim()) { toast.error("Escreva a resposta"); return; }
    setBusy(true);
    const body = {
      answer_text: form.answer_text, usage: form.usage, scope_type: form.scope_type,
      source_type: form.source_type,
      validity_days: form.usage === "temporary" ? Number(form.validity_days) : null,
      valid_until: form.usage === "until_date" ? form.valid_until : null,
      valid_from: form.usage === "from_date" ? form.valid_from : null,
      scope_targets: form.scope_targets ? form.scope_targets.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      await api.post(`/pending/${active.id}/answer`, body);
      toast.success("Resposta enviada ao funcionário pelo WhatsApp");
      setActive(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="pending-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Inbox size={22} className="text-indigo-600" /> Perguntas Pendentes
        </h1>
        <div className="flex items-center gap-2">
          <select data-testid="pending-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-input bg-white px-3 py-2 text-sm">
            {["pending", "assigned", "answered", "dismissed", "all"].map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
          <select data-testid="pending-priority-filter" value={priority} onChange={(e) => setPriority(e.target.value)}
            className="rounded-lg border border-input bg-white px-3 py-2 text-sm">
            <option value="">Todas prioridades</option>
            {["critical", "high", "normal", "low"].map((s) => <option key={s} value={s}>{PRIORITY_LABEL[s] || s}</option>)}
          </select>
        </div>
      </div>

      {!items.length && <p className="text-sm text-muted-foreground">Nenhuma pergunta pendente.</p>}

      <div className="space-y-3">
        {items.map((pq) => (
          <Card key={pq.id} className="p-4" data-testid={`pending-${pq.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{pq.question}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {pq.employee_name} · {DEPT_LABEL[pq.department_id] || pq.department_id || "geral"} {pq.client ? `· ${pq.client}` : ""} · motivo: <span className="text-foreground">{REASON_LABEL[pq.reason] || pq.reason}</span>
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge tone={PRIORITY_TONE[pq.priority]}>{PRIORITY_LABEL[pq.priority] || pq.priority}</Badge>
                  <Badge tone={STATUS_TONE[pq.status] || "muted"}>{STATUS_BADGE[pq.status] || pq.status}</Badge>
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Clock size={11} /> {String(pq.created_at).slice(0, 16).replace("T", " ")}</span>
                  {pq.assignee_name && <span className="text-[11px] text-muted-foreground">resp.: {pq.assignee_name}</span>}
                </div>
              </div>
              {pq.status !== "answered" && pq.status !== "dismissed" && (
                <div className="flex shrink-0 gap-2">
                  <button data-testid={`pending-assign-${pq.id}`} onClick={() => assign(pq)} title="Assumir" className="text-muted-foreground hover:text-primary"><UserCheck size={17} /></button>
                  <button data-testid={`pending-answer-open-${pq.id}`} onClick={() => openAnswer(pq)} title="Responder" className="text-zinc-400 hover:text-emerald-600"><Send size={17} /></button>
                  <button data-testid={`pending-dismiss-${pq.id}`} onClick={() => dismiss(pq)} title="Descartar" className="text-zinc-400 hover:text-red-600"><XCircle size={17} /></button>
                </div>
              )}
            </div>
            {pq.human_answer && <p className="mt-2 rounded-lg border border-zinc-200 bg-zinc-50 p-2 text-xs text-zinc-600">↳ {pq.human_answer}</p>}
          </Card>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="answer-modal" onClick={() => setActive(null)}>
          <Card className="w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg font-semibold">Responder</h2>
            <p className="mt-1 text-xs text-muted-foreground">{active.question}</p>
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Nada entra no cérebro automaticamente — você define uso, validade, escopo e natureza.
              </div>
              <Btn variant="ghost" onClick={getSuggestion} disabled={suggesting} data-testid="answer-suggest">
                {suggesting ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Sugerir (IA)
              </Btn>
            </div>
            {suggestion && (
              <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-[11px] text-indigo-700" data-testid="suggestion-banner">
                Sugestão: <b>{USAGE_LABEL[suggestion.usage] || suggestion.usage}</b>{suggestion.validity_days ? ` (${suggestion.validity_days}d)` : ""} · escopo <b>{SCOPE_LABEL[suggestion.scope_type] || suggestion.scope_type}</b> · natureza <b>{SOURCE_LABEL[suggestion.source_type] || suggestion.source_type}</b>
                {suggestion.needs_approval && <span className="ml-1 rounded bg-red-100 px-1 text-red-700">requer aprovação</span>}
                {suggestion.reason ? <div className="mt-0.5 text-zinc-500">Motivo: {suggestion.reason}</div> : null}
                <div className="mt-0.5 text-zinc-500">Revise e edite — nada é publicado automaticamente.</div>
              </div>
            )}
            <textarea data-testid="answer-text" value={form.answer_text} onChange={(e) => setForm({ ...form, answer_text: e.target.value })}
              rows={4} placeholder="Resposta ao funcionário..." className="mt-3 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="text-xs text-muted-foreground">Uso da resposta
                <select data-testid="answer-usage" value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-white px-2 py-2 text-sm text-foreground">
                  {USAGE.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                </select>
              </label>
              <label className="text-xs text-muted-foreground">Natureza
                <select data-testid="answer-source-type" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-white px-2 py-2 text-sm text-foreground">
                  {SOURCE_TYPES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>)}
                </select>
              </label>
              {form.usage === "temporary" && (
                <label className="text-xs text-muted-foreground">Dias de validade
                  <Input data-testid="answer-days" type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} className="mt-1" />
                </label>
              )}
              {form.usage === "until_date" && (
                <label className="text-xs text-muted-foreground">Válido até
                  <Input data-testid="answer-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="mt-1" />
                </label>
              )}
              {form.usage === "from_date" && (
                <label className="text-xs text-muted-foreground">Válido a partir de
                  <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="mt-1" />
                </label>
              )}
              {form.usage !== "only_conversation" && form.usage !== "no_reuse" && (
                <>
                  <label className="text-xs text-muted-foreground">Escopo
                    <select data-testid="answer-scope" value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-white px-2 py-2 text-sm text-foreground">
                      {SCOPES.map((s) => <option key={s} value={s}>{SCOPE_LABEL[s] || s}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-muted-foreground">Alvos (ids, opcional)
                    <Input value={form.scope_targets} onChange={(e) => setForm({ ...form, scope_targets: e.target.value })} placeholder="id1, id2" className="mt-1" />
                  </label>
                </>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setActive(null)}>Cancelar</Btn>
              <Btn data-testid="answer-submit" onClick={submitAnswer} disabled={busy}>{busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Enviar ao funcionário</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
